import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCountTimer } from '../index';
import type { LoggerHostDeps } from './hostDeps';

// Grace window after a countdown reaches 0: the banner shows "✓ Done" for
// this long, then auto-hides (inbox 477e9ed2/0b1a7474 — the rest timer used
// to sit at 0:00 until you skipped it). A +X tap during the window re-arms
// instead of hiding (Antenore 2026-07-02).
const AUTO_HIDE_MS = 3000;

// Mobile rest-timer hook. A thin count-DOWN adapter over the generic
// useCountTimer engine (2026-06-05 generalization, Antenore's call): the
// deadline math, pause/resume and ±60s live in useCountTimer; this file only
// wires the rest-specific bits — the notifee cue and the exercise-slug tag.
//
// Audio + vibration cue routing (1.97.6): notifee is the SINGLE source of
// truth. The previous JS-side path (Vibration.vibrate + react-native-sound)
// was removed because:
//
// 1. react-native-sound on Android uses MediaPlayer with
//    requestAudioFocus(AUDIOFOCUS_GAIN) — a *permanent* focus request. The
//    library never calls release() after playback, so MediaPlayer holds focus
//    indefinitely → Spotify never resumes. Routing the cue through Android's
//    NotificationManager instead uses AUDIOFOCUS_GAIN_TRANSIENT (auto-released
//    by the system after the sound ends → Spotify resumes).
//
// 2. Sound.setCategory() is documented iOS-only (react-native-sound GitHub
//    #287, #551). There is no JS-side knob to change the Android audio stream
//    short of patching the native code.
//
// Notifee schedules the notification against the wall-clock target; when it
// posts, Android plays the channel sound via the notification stream and
// manages focus correctly. Foreground + tray cue land simultaneously since
// notifee fires at the same wall-clock instant the in-app countdown reaches 0.

type RestTimerState = {
  endsAt: number | null;
  remainingSec: number;
  totalSec: number;
  isPaused: boolean;
  /** Countdown reached 0 and we're in the brief "✓ Done" grace window
   *  before the banner auto-hides. A +X tap during it re-arms the timer
   *  and drops this flag. */
  finished: boolean;
  /** Exercise slug the active rest belongs to, set by the most recent
   *  start() call. Drives the in-banner "save as default" quick-edit
   *  affordance (1.81.0) — when the user wants to bake the rest they just took
   *  as the catalog default for this lift, the picker reads this slug to know
   *  which row to PATCH. Null while idle. */
  exerciseId: string | null;
};

export type UseRestTimer = {
  state: RestTimerState;
  start: (seconds: number, exerciseId?: string) => void;
  add: (seconds: number) => void;
  reset: () => void;
  pause: () => void;
  resume: () => void;
};

export function useRestTimer(
  timerNotification?: LoggerHostDeps['timerNotification'],
): UseRestTimer {
  const timer = useCountTimer({
    onSchedule: (deadlineMs) =>
      void timerNotification?.schedule('rest', deadlineMs),
    onCancel: () => void timerNotification?.cancel('rest'),
  });

  const [finished, setFinished] = useState(false);
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Access reset() without depending on the timer object identity (which
  // changes every tick) so the auto-hide effect only re-runs on real state
  // transitions.
  const resetRef = useRef(timer.reset);
  resetRef.current = timer.reset;

  const clearHide = useCallback(() => {
    if (hideRef.current !== null) {
      clearTimeout(hideRef.current);
      hideRef.current = null;
    }
  }, []);

  const { endsAt, remainingSec, isPaused, atZero } = timer.state;
  useEffect(() => {
    if (atZero && !finished) {
      // Countdown just hit 0 → enter the grace window, arm the auto-hide.
      setFinished(true);
      clearHide();
      hideRef.current = setTimeout(() => {
        hideRef.current = null;
        setFinished(false);
        resetRef.current();
      }, AUTO_HIDE_MS);
    } else if (!atZero && finished) {
      // Re-armed (remaining > 0 via +X) or cleared (endsAt null) → leave the
      // grace window and cancel the pending auto-hide.
      setFinished(false);
      clearHide();
    }
  }, [atZero, finished, clearHide]);

  useEffect(() => clearHide, [clearHide]);

  return useMemo(
    () => ({
      state: {
        endsAt,
        remainingSec,
        totalSec: timer.state.totalSec,
        isPaused,
        finished,
        exerciseId: timer.state.tag,
      },
      start: (seconds, exerciseId) =>
        timer.start({ seconds, mode: 'down', tag: exerciseId }),
      add: timer.add,
      reset: timer.reset,
      pause: timer.pause,
      resume: timer.resume,
    }),
    [timer, endsAt, remainingSec, isPaused, finished],
  );
}
